import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { Button, LangToggle, Screen } from '@dia/ui';
import { useAuth } from '../auth';

/**
 * Stay code plus last name, and the consent screen in the same step, because
 * Ley 81 wants the consent before anything personal is processed and this is
 * the moment before anything personal is processed.
 */
export function SignIn() {
  const { t, lang } = useT();
  const { signIn, loading, error } = useAuth();
  const [code, setCode] = useState('');
  const [lastName, setLastName] = useState('');
  const [consent, setConsent] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // Ley 81: no consent, no sign in. The public pages stay open without one.
    if (!consent) return;
    await signIn(code, lastName, consent);
  };

  return (
    <Screen title={t('guest.login.title')} actions={<LangToggle />}>
      <form onSubmit={onSubmit} className="flex flex-col gap-6 mt-4">
        <p className="text-inkMuted">{t('guest.login.intro')}</p>

        <label className="flex flex-col gap-2">
          <span>{t('guest.login.code_label')}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            maxLength={12}
            required
            className="min-h-tap bg-surface border border-line rounded px-4 tracking-[0.2em] text-ink"
          />
          <span className="text-inkFaint text-caption">{t('guest.login.code_help')}</span>
        </label>

        <label className="flex flex-col gap-2">
          <span>{t('guest.login.lastname_label')}</span>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            required
            className="min-h-tap bg-surface border border-line rounded px-4 text-ink"
          />
        </label>

        <div className="bg-surface border border-line rounded p-4 flex flex-col gap-3">
          <h2 className="text-[1.1em]">{t('guest.consent.title')}</h2>
          <p className="text-inkMuted text-caption">{t('guest.consent.body')}</p>
          <p className="text-inkMuted text-caption">{t('guest.consent.points')}</p>
          <p className="text-inkFaint text-caption">{t('guest.consent.law')}</p>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-5 w-5"
            />
            <span>{t('guest.consent.accept')}</span>
          </label>
        </div>

        {error && (
          <p role="alert" className="text-danger">
            {lang === 'es' ? error.es : error.en}
          </p>
        )}

        {!consent && <p className="text-inkFaint text-caption">{t('guest.consent.required')}</p>}

        <Button type="submit" block size="lg" busy={loading} busyLabel={t('loading')} disabled={!consent}>
          {t('guest.login.submit')}
        </Button>

        <Link to="/llegada" className="text-accent underline underline-offset-4 text-center min-h-tap inline-flex items-center justify-center">
          {t('guest.login.read_arrive')}
        </Link>
      </form>
    </Screen>
  );
}
